"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Hand, ArrowUpRight, Globe2, Plus, Menu, X, Bell, Bookmark } from "lucide-react";
import { useState } from "react";
import { useApp } from "./provider";
export function Shell({ children }: { children: React.ReactNode }) {
  const { t, locale, setLocale, user } = useApp(),
    pathname = usePathname(),
    [open, setOpen] = useState(false);
  const links = [
      ["/", t("Home", "首页")],
      ["/dictionary", t("Dictionary", "手语词典")],
    ["/languages", t("Sign languages", "手语语言")],
    ["/about", t("Our community", "我们的社区")],
  ];
  return (
    <>
      <a className="skip" href="#main">
        {t("Skip to content", "跳转到正文")}
      </a>
      <header className="header">
        <Link href="/" className="brand" aria-label="KnowYourSign">
          <span className="brand-icon">
            <img src="/logo.png" alt="" />
          </span>
          <span>
            KnowYour<span className="brand-sign">Sign</span>
            <small>
              {t(
                "A living language. A shared world.",
                "鲜活的语言，共享的世界。",
              )}
            </small>
          </span>
        </Link>
        <nav
          className={open ? "nav open" : "nav"}
          aria-label={t("Main navigation", "主导航")}
        >
          {links.map(([url, label]) => (
            <Link
              key={url}
              href={url}
              onClick={() => setOpen(false)}
              className={pathname === url ? "active" : ""}
            >
              {label}
            </Link>
          ))}
        </nav>
        <div className="header-actions">
          <button
            className="locale"
            onClick={() => setLocale(locale === "en" ? "zh" : "en")}
            aria-label={t("Switch to Chinese", "切换到英语")}
          >
            <Globe2 size={17} />
            {locale === "en" ? "中文" : "English"}
          </button>
          {user ? (
            <>
              <Link
                className="icon-button notification-link"
                href="/notifications"
                aria-label={t("Notifications", "通知")}
              >
                <Bell size={19} />
              </Link>
              <Link
                className="icon-button notification-link"
                href="/saved"
                aria-label={t("Saved signs", "已收藏的手语")}
              >
                <Bookmark size={19} />
              </Link>
              <Link
                href="/profile"
                className="avatar"
                aria-label={t("My profile", "我的资料")}
              >
                {user.username.slice(0, 1).toUpperCase()}
              </Link>
            </>
          ) : (
            <Link className="signin" href="/auth">
              {t("Sign in", "登录")}
            </Link>
          )}
          <Link className="button primary compact upload-link" href="/upload">
            <Plus size={17} />
            {t("Add Your Sign", "添加你的手语")}
          </Link>
          <button
            className="icon-button mobile-menu"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={t("Menu", "菜单")}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      <main id="main">{children}</main>
      <footer>
        <div className="footer-brand">
          <Hand size={20} />
          <strong>KnowYourSign</strong>
          <span>
            {t("Language grows when we share it.", "语言因分享而生长。")}
          </span>
        </div>
        <div>
          <Link href="/about">{t("About the project", "关于项目")}</Link>
          <Link href="/about#guidelines">
            {t("Community guidelines", "社区准则")}
          </Link>
          <Link href="/about#privacy">{t("Privacy", "隐私")}</Link>
          <span>English · 中文</span>
        </div>
      </footer>
    </>
  );
}
export function PageTitle({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-title">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}
export function Empty({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty">
      <Hand size={35} />
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}
export function Gate({
  children,
  admin = false,
}: {
  children: React.ReactNode;
  admin?: boolean;
}) {
  const { user, ready, t } = useApp();
  if (!ready)
    return (
      <div className="loading" role="status">
        {t("Loading…", "加载中…")}
      </div>
    );
  if (!user)
    return (
      <div className="container narrow">
        <Empty
          title={t("Your community is waiting", "社区期待您的加入")}
          description={t(
            "Create an account to share signs, support variations, and build the dictionary together.",
            "创建账户，分享手语、支持不同变体，共同建设词典。",
          )}
          action={
            <Link href="/auth" className="button primary">
              {t("Sign in or join", "登录或注册")}
              <ArrowUpRight size={16} />
            </Link>
          }
        />
      </div>
    );
  if (admin && !["admin", "moderator"].includes(user.role))
    return (
      <div className="container narrow">
        <Empty
          title={t("Administrator access required", "需要管理员权限")}
          description={t(
            "This workspace is reserved for the moderation team.",
            "此工作区仅供审核团队使用。",
          )}
        />
      </div>
    );
  return <>{children}</>;
}
